/**
 * Mixpanel feature-gate keys — the ONE list both sides read (RAP-668).
 *
 * The frontend gates UI on these; the backend's `GET /feature-flags` evaluates them against
 * Mixpanel server-side (so a browser ad blocker cannot empty the portal) and refuses to evaluate
 * any key that is not listed here. Add a flag here, not in either app.
 */

/** RF Financials MVP. */
export const FINANCIALS_MVP_FLAG = "financials_mvp";

/** Dual Rep / Duplicate Check rollout. */
export const DUAL_REP_FLAG = "dual_rep";

/**
 * KILL SWITCH for the AI chat assistant: chat is hidden for a user when this gate is ON.
 * Deliberately the opposite polarity to every other flag — see the frontend's featureFlags.ts.
 * NEVER give it a `handled_staff` rule (RAP-801): ON switches chat OFF for every staff member.
 */
export const CHAT_KILL_SWITCH_FLAG = "ai_chat_disabled";

/**
 * RAP-736: the Social Media Harm charts and the Leads table's Injury Tier read the STANDARDIZED
 * answers (falling back to the raw payload per lead) instead of every raw payload. Off = the
 * original raw-payload path, unchanged. Evaluated server-side per viewer (firm targeting in
 * Mixpanel); a missing flag or a Mixpanel failure reads as off.
 */
export const STANDARDIZED_ANSWERS_CHARTS_FLAG = "standardized_answers_charts";

/**
 * RAP-736: the portal lead detail's Intake tab also offers the questionnaire read from the
 * STANDARDIZED answers, beside the original (an Original / Standardized switch, Original by
 * default), so both can be compared on the same lead. Off = the original only, unchanged.
 * Evaluated server-side; a missing flag or a Mixpanel failure reads as off.
 */
export const STANDARDIZED_ANSWERS_INTAKE_FLAG = "standardized_answers_intake";

/**
 * RAP-757: portal chat also offers tools that query leads by their STANDARDIZED answers
 * (`list_standard_questions`, `query_standard_answers`, `get_lead_standard_answers`), beside
 * today's `query_leads` / `get_lead_details`, so both can be compared on the same questions.
 * Off = today's chat tools and prompt, unchanged. Evaluated server-side by the chat route; a
 * missing flag or a Mixpanel failure reads as off.
 */
export const STANDARDIZED_ANSWERS_CHAT_FLAG = "standardized_answers_chat";

/**
 * RAP-741: "What Relay did for you" in the main app (law firms, marketing partners). Fails closed
 * like the portal sections: the nav item and the page stay hidden until the gate exists and is on.
 * The portal's own entry rides `PORTAL_SECTION_FLAGS.relayImpact`; the superadmin page is ungated.
 */
export const RELAY_IMPACT_FLAG = "relay_impact";

/** One gate per referring-portal section. See the frontend's featureFlags.ts for semantics. */
export const PORTAL_SECTION_FLAGS = {
  acquisition: "portal_acquisition",
  campaigns: "portal_campaigns",
  capital: "portal_capital",
  cases: "portal_cases",
  intake: "portal_intake",
  leads: "portal_leads",
  relayImpact: "portal_relay_impact",
  settings: "portal_settings",
  summary: "portal_summary",
} as const;

export type PortalSectionFlag = (typeof PORTAL_SECTION_FLAGS)[keyof typeof PORTAL_SECTION_FLAGS];

/** Every flag the app knows. The backend evaluates only these. */
export const ALL_FEATURE_FLAGS: readonly string[] = [
  FINANCIALS_MVP_FLAG,
  DUAL_REP_FLAG,
  CHAT_KILL_SWITCH_FLAG,
  STANDARDIZED_ANSWERS_CHARTS_FLAG,
  STANDARDIZED_ANSWERS_INTAKE_FLAG,
  STANDARDIZED_ANSWERS_CHAT_FLAG,
  RELAY_IMPACT_FLAG,
  ...Object.values(PORTAL_SECTION_FLAGS),
];
